/**
 * @file LifetimeCurve.ts
 * @role Pure keyframe interpolation for driving a numeric property (alpha, scale, ...) over a
 *       particle's normalized lifetime `t` in [0, 1]. No side effects, no Pixi dependency.
 * @owns The `CurveKeyframe` shape and the `sampleCurve` interpolation function.
 * @reads Nothing (pure function of its arguments).
 * @neverDoes Mutate its input, read wall-clock time, or know about particles/sprites.
 * @see src/core/particles/ParticleService.ts (consumer)
 */

/** A single point on a lifetime curve: `t` is normalized lifetime in [0, 1], `v` is the value. */
export interface CurveKeyframe {
  readonly t: number;
  readonly v: number;
}

/**
 * Linearly interpolates `curve` at normalized time `t`, clamping `t` to [0, 1] and clamping to
 * the first/last keyframe's value outside the curve's own t-range. Assumes `curve` is sorted
 * ascending by `t` (config-authored curves are expected to already be sorted; this function does
 * not sort or validate, keeping it a cheap per-particle-per-frame call).
 */
export function sampleCurve(curve: readonly CurveKeyframe[], t: number): number {
  if (curve.length === 0) return 1;
  const first = curve[0]!;
  if (curve.length === 1) return first.v;

  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  if (clamped <= first.t) return first.v;

  const last = curve[curve.length - 1]!;
  if (clamped >= last.t) return last.v;

  for (let i = 0; i < curve.length - 1; i += 1) {
    const a = curve[i]!;
    const b = curve[i + 1]!;
    if (clamped >= a.t && clamped <= b.t) {
      const span = b.t - a.t;
      const localT = span === 0 ? 0 : (clamped - a.t) / span;
      return a.v + (b.v - a.v) * localT;
    }
  }
  return last.v;
}
